import { css } from "@emotion/react";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import * as React from "react";

import { useCookieConsent } from "@zapier/cookies";
import { Colors } from "@zapier/design-system";
import { useCurrentAccountId } from "@zapier/identity";
import {
  ConfirmModalProvider,
  DefaultErrorMessage,
  ErrorBoundary,
  HeldRunsBanner,
  NotificationProvider,
  RunStepTroubleshootDetailContextProvider,
  TooltipWrapperTarget,
} from "@zapier/reporting";
import { useEmitEvent } from "@zapier/reporting/src/hooks";
import { UniversalLayout } from "@zapier/universal-layout";

import { FullStory } from "../observability";
import { getResponsiveStyles, useRequireAuth } from "../utils";

const DeferredGlobalSearchInput = dynamic(
  () =>
    import("@zapier/global-search").then(
      ({ GlobalSearchInput }) => GlobalSearchInput
    ),
  { ssr: false }
);

const renderUniversalSearch = () => <DeferredGlobalSearchInput />;

type Props = {
  children: React.ReactNode;
  hideHeldRunsBanner?: boolean;
  backgroundColor?: string;
};

const rootStyle = (backgroundColor?: string) =>
  getResponsiveStyles({
    mobile: css`
      background-color: ${!backgroundColor
        ? Colors.PrimeWhite
        : backgroundColor};
      box-sizing: border-box;
      min-height: 100%;
      padding: 29px 20px 58px 20px;
    `,
    tablet: css`
      padding: 29px 40px 58px 40px;
    `,
    desktop: css`
      padding: 29px 40px;
    `,
  });

const mainStyle = css`
  margin: 0 auto;
  max-width: 1120px;
`;

function ErrorFallbackWrapper(backgroundColor?: string) {
  return function ErrorFallback() {
    return (
      <div css={rootStyle(backgroundColor)}>
        <DefaultErrorMessage />
      </div>
    );
  };
}

export function PageLayout(props: Props) {
  const currentAccountId = useCurrentAccountId();
  const { emitEvent } = useEmitEvent();
  const router = useRouter();
  const cookieCategories = useCookieConsent();

  useRequireAuth();

  return (
    <>
      <UniversalLayout
        currentUrl={router.asPath}
        showSimplifiedFooter={true}
        renderUniversalSearch={renderUniversalSearch}
        trackEvent={(eventName, meta) => {
          emitEvent({
            eventName: eventName,
            meta: {
              ...meta,
            },
          });
        }}
      >
        <ErrorBoundary fallback={ErrorFallbackWrapper(props.backgroundColor)}>
          <ConfirmModalProvider>
            <NotificationProvider>
              <RunStepTroubleshootDetailContextProvider>
                <FullStory
                  accountId={currentAccountId}
                  disableFullStory={!cookieCategories?.byName?.analytics}
                />
                {!props.hideHeldRunsBanner && <HeldRunsBanner />}
                <div css={rootStyle(props.backgroundColor)}>
                  <div css={mainStyle}>{props.children}</div>
                </div>
              </RunStepTroubleshootDetailContextProvider>
            </NotificationProvider>
          </ConfirmModalProvider>
        </ErrorBoundary>
      </UniversalLayout>

      <TooltipWrapperTarget />
    </>
  );
}
